import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { auth } from '../services/auth'
import { Screen } from '../components/lease/Screen'
import { BrandHeading } from '../components/lease/BrandHeading'
import { PrimaryButton } from '../components/lease/PrimaryButton'

// Stripe redirects here (mockup 7). Success: the webhook credits the account a
// beat after redirect, so poll /me until the balance shows up (~30 s cap).
const POLL_MS = 2000
const MAX_POLLS = 15

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="h-8 w-8" aria-hidden="true">
      <path d="M5 10.5l3.2 3.2L15 6.5" stroke="currentColor" strokeWidth="2.4"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PaymentStatus() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const status = params.get('status')
  const [credits, setCredits] = useState(null)
  const [timedOut, setTimedOut] = useState(false)
  const polls = useRef(0)

  useEffect(() => {
    if (status !== 'success') return undefined
    let cancelled = false
    const tick = async () => {
      polls.current += 1
      try {
        const me = await auth.me()
        if (cancelled) return
        if (me.credit_balance > 0) {
          setCredits(me.credit_balance)
          return
        }
      } catch {
        /* keep polling */
      }
      if (polls.current >= MAX_POLLS) {
        setTimedOut(true)
        return
      }
      timer = setTimeout(tick, POLL_MS)
    }
    let timer = setTimeout(tick, POLL_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [status])

  if (status === 'cancelled') {
    return (
      <Screen className="text-center">
        <BrandHeading className="text-3xl">Payment was cancelled</BrandHeading>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          No charge was made. You can pick a plan whenever you're ready.
        </p>
        <PrimaryButton className="mt-8" onClick={() => navigate('/onboarding')}>
          Back to start
        </PrimaryButton>
      </Screen>
    )
  }

  if (credits !== null) {
    return (
      <Screen className="text-center">
        <span className="mx-auto flex h-16 w-16 animate-fade-in items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg">
          <CheckIcon />
        </span>
        <BrandHeading className="mt-5 text-3xl">Payment Successful</BrandHeading>
        <div className="mt-4 animate-slide-in space-y-2 rounded-2xl bg-white/70 p-5 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700">
          <p className="text-slate-700 dark:text-slate-200">
            Your payment has been processed successfully.
          </p>
          <p className="text-slate-700 dark:text-slate-200">
            You have{' '}
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">
              {credits} analysis credits
            </span>{' '}
            (valid for 30 days).
          </p>
        </div>
        <PrimaryButton className="mt-8 animate-slide-in" onClick={() => navigate('/lease-analysis')}>
          Continue
        </PrimaryButton>
      </Screen>
    )
  }

  if (timedOut) {
    return (
      <Screen className="text-center">
        <BrandHeading className="text-3xl">Payment is processing…</BrandHeading>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          Your credits should appear shortly. If they don't show up in a few minutes,
          contact{' '}
          <a className="text-emerald-700 underline dark:text-emerald-400" href="mailto:support@coloradoleasecheck.com">
            support@coloradoleasecheck.com
          </a>
          .
        </p>
        <PrimaryButton className="mt-8" onClick={() => navigate('/dashboard')}>
          Go to dashboard
        </PrimaryButton>
      </Screen>
    )
  }

  return (
    <Screen className="text-center">
      <BrandHeading className="text-3xl">Confirming your payment…</BrandHeading>
      <p className="mt-3 text-slate-600 dark:text-slate-300">This usually takes a few seconds.</p>
    </Screen>
  )
}

export default PaymentStatus
